import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { X, Lock } from 'lucide-react';

const SetNewPassword = ({ onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Failed to change password');
        return;
      }

      // Success - notify parent
      onSuccess();
    } catch (err) {
      setError('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 max-w-md w-full border border-slate-700 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-900/30 rounded-full">
              <Lock className="w-6 h-6 text-yellow-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Set New Password</h2>
          </div>
        </div>

        <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-600 rounded-lg">
          <p className="text-sm text-yellow-300">
            <strong>⚠️ Security Required:</strong> You're using a temporary password. Please set a new secure password to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-600 rounded-lg">
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <div>
            <label className="text-sm text-gray-400 mb-2 block">New Password</label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full py-3 bg-slate-900/50 border-slate-700 text-white"
              required
              minLength={6}
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Confirm New Password</label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full py-3 bg-slate-900/50 border-slate-700 text-white"
              required
              minLength={6}
            />
          </div>

          <div className="p-3 bg-blue-900/20 border border-blue-600 rounded-lg">
            <p className="text-xs text-blue-300">
              Password must be at least 6 characters long
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full py-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold"
          >
            {loading ? 'Updating...' : 'Set New Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SetNewPassword;
